import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import CoperturaMap, { coverageColor } from '../components/dashboard/CoperturaMap';

const bontaOf = (r) => {
  const ctrl = (r.tim || 0) + (r.non_tim || 0);
  return ctrl ? Math.round((r.non_tim / ctrl) * 100) : null;
};

// colori degli stati dei controllati: NON TIM = lead (verde), TIM = grigio, Errori = ambra
const STATE_COL = { non_tim: '#22c55e', tim: '#64748b', errore: '#f59e0b' };
// cella per singolo stato: percentuale + barretta grafica che la rappresenta + conteggio
function StateCell({ v, tot, color }) {
  const n = v || 0;
  const pct = tot > 0 ? Math.round((n / tot) * 100) : 0;
  return (
    <td className="aa-num">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
        <div style={{ fontSize: 13, whiteSpace: 'nowrap' }}>
          <span style={{ color, fontWeight: 800 }}>{pct}%</span>
          <span style={{ color: 'var(--muted)', fontSize: 11, marginLeft: 5 }}>{n.toLocaleString('it-IT')}</span>
        </div>
        <div style={{ width: 60, height: 5, borderRadius: 3, background: 'var(--line)', overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width .3s ease' }} />
        </div>
      </div>
    </td>
  );
}

export default function CoperturaPage() {
  const [data, setData] = useState({ province: [], regioni: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [tipo, setTipo] = useState('');            // '' | 'azienda' | 'privato'
  const [drillRegion, setDrillRegion] = useState(null);  // regione selezionata dalla mappa
  const [selected, setSelected] = useState(null);        // regione SELEZIONATA (click) -> pannello a destra

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/dashboard/copertura`, { params: { tipo_impresa: tipo || undefined } })
      .then(r => setData(r.data || { province: [], regioni: [], summary: {} }))
      .catch(() => toast.error('Errore caricamento copertura'))
      .finally(() => setLoading(false));
  }, [tipo]);
  useEffect(() => { load(); }, [load]);

  const provRows = (data.province || []).map(p => ({ ...p, bonta: bontaOf(p) }));

  // rollup per regione (la mappa mostra SEMPRE le regioni). MEMOIZZATO: se non cambiano i dati,
  // il riferimento resta stabile -> react-leaflet non ri-applica lo stile base cancellando la
  // selezione grigia quando si ri-renderizza la pagina (era la causa del "doppio click").
  const mapData = useMemo(() => {
    const regAgg = {};
    (data.province || []).forEach(p => {
      const a = regAgg[p.regione] || (regAgg[p.regione] = { regione: p.regione, importati: 0, tim: 0, non_tim: 0 });
      a.importati += (p.importati || 0); a.tim += (p.tim || 0); a.non_tim += (p.non_tim || 0);
    });
    const md = {};
    Object.values(regAgg).forEach(r => {
      md[r.regione.toUpperCase()] = { importati: r.importati, controllati: r.tim + r.non_tim, non_tim: r.non_tim, bonta: bontaOf(r) };
    });
    return md;
  }, [data]);

  // clic su una regione -> pannello a destra + province sotto (stabile per non far re-renderizzare la mappa)
  const onAreaSelect = useCallback((info, name) => { setSelected(info); setDrillRegion(name); }, []);

  const baseProv = drillRegion ? provRows.filter(p => p.regione === drillRegion) : provRows;
  const scoperte = baseProv.filter(p => p.importati === 0);
  const bassaBonta = baseProv.filter(p => p.importati > 0 && p.controllati >= 15 && (p.bonta ?? 100) < 70);
  const regioniConBuchi = (data.regioni || []).filter(r => r.scoperte > 0);
  const tableRows = [...baseProv].sort((a, b) => (a.importati - b.importati) || ((a.bonta ?? 999) - (b.bonta ?? 999)));

  const kpis = [
    { lab: 'Province scoperte (0)', val: `${scoperte.length}/${baseProv.length}`, col: 'var(--err)' },
    { lab: 'Bontà sotto 70%', val: bassaBonta.length, col: 'var(--warn)' },
    { lab: 'Regioni con buchi', val: regioniConBuchi.length, col: '#0B5794' },
  ];

  return (
    <div style={{ height: 'calc(100vh - 96px)', display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden' }}>
      {/* KPI + filtro (niente titolo: c'è già nell'header) */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'stretch', flex: '0 0 auto' }}>
        {kpis.map(k => (
          <div key={k.lab} style={{ flex: '1 1 170px', background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 10, padding: '10px 16px' }}>
            <div style={{ fontSize: 12, color: 'var(--muted)' }}>{k.lab}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: k.col }}>{k.val}</div>
          </div>
        ))}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <select value={tipo} onChange={e => setTipo(e.target.value)} style={{ padding: '8px 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 13 }}>
            <option value="">Tutti</option>
            <option value="azienda">Aziende</option>
            <option value="privato">Privati</option>
          </select>
        </div>
      </div>

      {/* Mappa regioni (widget a tutta larghezza) */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 8, flex: '0 0 auto', position: 'relative' }}>
        <div style={{ height: '33vh', minHeight: 240 }}>
          {loading ? <div style={{ padding: 40, textAlign: 'center', color: 'var(--faint)' }}>Caricamento…</div>
            : <CoperturaMap mode="regioni" data={mapData} onAreaSelect={onAreaSelect} />}
        </div>
        {/* Pannello dettagli regione: appare a destra (spazio vuoto della mappa) con dissolvenza */}
        {!loading && (
          <div style={{ position: 'absolute', top: 16, right: 18, bottom: 46, width: '38%', maxWidth: 360, minWidth: 210, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            {selected ? (
              <div key={selected.raw} className="cop-hover-card">
                {selected.shape && (
                  <svg className="chc-silhouette" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
                    <path d={selected.shape} />
                  </svg>
                )}
                <div className="chc-content">
                  <div className="chc-title">{selected.raw}</div>
                  {selected.vuota ? (
                    <div className="chc-empty">Mai importata (0)</div>
                  ) : (
                    <div className="chc-rows">
                      <div><span>Importati</span><b>{selected.importati.toLocaleString('it-IT')}</b></div>
                      <div><span>Controllati</span><b>{selected.controllati.toLocaleString('it-IT')}</b></div>
                      <div><span>NON TIM</span><b>{selected.non_tim.toLocaleString('it-IT')}</b></div>
                      <div style={{ borderBottom: 0 }}><span>Bontà</span><b>{selected.bonta}</b></div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="cop-hover-hint">Clicca una regione per vederne i dettagli</div>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', fontSize: 11, color: 'var(--muted)', padding: '8px 6px 2px' }}>
          <span style={{ color: 'var(--faint)' }}>Clicca una regione per vedere le sue province nella tabella.</span>
          <span style={{ marginLeft: 'auto' }}><i style={{ background: 'var(--line)', display: 'inline-block', width: 10, height: 10, borderRadius: 2, marginRight: 4 }} />Mai importata</span>
          <span><i style={{ background: 'var(--err)', display: 'inline-block', width: 10, height: 10, borderRadius: 2, marginRight: 4 }} />&lt;40%</span>
          <span><i style={{ background: 'var(--warn)', display: 'inline-block', width: 10, height: 10, borderRadius: 2, marginRight: 4 }} />&lt;55%</span>
          <span><i style={{ background: 'var(--warn)', display: 'inline-block', width: 10, height: 10, borderRadius: 2, marginRight: 4 }} />&lt;70%</span>
          <span><i style={{ background: 'var(--ok)', display: 'inline-block', width: 10, height: 10, borderRadius: 2, marginRight: 4 }} />≥70%</span>
        </div>
      </div>

      {/* Tabella province (widget) */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12, padding: 8, flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 6px 10px', flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14, color: 'var(--text)' }}>
            {drillRegion ? `Province · ${drillRegion}` : 'Tutte le province'}
          </strong>
          {drillRegion && (
            <button className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => setDrillRegion(null)}>
              ← Tutte le regioni
            </button>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--muted)' }}>{tableRows.length} province</span>
        </div>
        <div style={{ overflowX: 'auto', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          <table className="aa-table cop-table">
            <thead><tr>
              <th className="aa-name">Provincia</th>
              <th className="aa-num">Importati</th><th className="aa-num">Controllati</th>
              <th className="aa-num" style={{ color: STATE_COL.non_tim, minWidth: 96 }}>NON TIM</th>
              <th className="aa-num" style={{ color: STATE_COL.tim, minWidth: 96 }}>TIM</th>
              <th className="aa-num" style={{ color: STATE_COL.errore, minWidth: 96 }}>Errori</th>
              <th className="aa-num">Bontà</th><th className="aa-num">Stato</th>
            </tr></thead>
            <tbody>
              {tableRows.map(p => {
                const verif = (p.tim || 0) + (p.non_tim || 0) + (p.errore || 0);
                return (
                <tr key={p.provincia}>
                  <td className="aa-name">{p.provincia}</td>
                  <td className="aa-num">{p.importati.toLocaleString('it-IT')}</td>
                  <td className="aa-num">{verif.toLocaleString('it-IT')}</td>
                  <StateCell v={p.non_tim} tot={verif} color={STATE_COL.non_tim} />
                  <StateCell v={p.tim} tot={verif} color={STATE_COL.tim} />
                  <StateCell v={p.errore} tot={verif} color={STATE_COL.errore} />
                  <td className="aa-num" style={{ color: coverageColor({ importati: p.importati, controllati: p.controllati, bonta: p.bonta }), fontWeight: 700 }}>
                    {p.importati === 0 || p.controllati < 15 ? '—' : `${p.bonta}%`}
                  </td>
                  <td className="aa-num">
                    {p.importati === 0 ? <span style={{ color: 'var(--err)', fontWeight: 700 }}>SCOPERTA</span>
                      : (p.controllati >= 15 && (p.bonta ?? 100) < 70) ? <span style={{ color: 'var(--warn)', fontWeight: 700 }}>BASSA</span>
                      : <span style={{ color: 'var(--ok)' }}>ok</span>}
                  </td>
                </tr>
                );
              })}
              {tableRows.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 24, color: 'var(--faint)' }}>Nessuna provincia</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
